import React from 'react';
import { 
  brandBlue, 
  textDark, 
  background, 
  border, 
  white, 
  container,
  heroPadding
} from '../styles/theme';

const Home = () => {
  const services = [
    {
      title: "CloudFront & CDN Solutions",
      icon: "🌐",
      description: "Enterprise-scale CDN solutions with AWS partnership expertise",
      features: [
        "Terabytes of data transferred monthly for enterprise clients",
        "Edge access protection with tokens, IP whitelisting, and geo-blocking",
        "Custom dashboards and dedicated support team",
        "Dynamic CDN selector for real-time optimization",
        "Multi-CDN support for better performance"
      ]
    },
    {
      title: "Video Engineering",
      icon: "🎬",
      description: "Advanced video workflows and encoding solutions for any scale",
      features: [
        "Custom VOD and live workflows for cloud and on-premise",
        "Multi-codec support: H.264, H.265, and AV1",
        "DASH and HLS streaming optimization",
        "Enterprise DRM and secure token authentication",
        "Advanced watermarking and content protection"
      ]
    },
    {
      title: "Cloud Migration",
      icon: "☁️",
      description: "Seamless migration from any platform to cloud infrastructure",
      features: [
        "100,000+ hours of migrated VOD content",
        "20+ live channels migrated with zero downtime",
        "Minimal disruption thanks to comprehensive planning",
        "Custom API services for data processing and workflows",
        "Migrated customers: Disc Golf Network, TRT, Rádio Renascença & more",
      ]
    },
    {
      title: "OTT Headend Solutions",
      icon: "📡",
      description: "Complete OTT headend deployment and management services",
      features: [
        "Custom on-premises headend implementations",
        "AWS Elemental, Ateme, Dell, and NetApp integration",
        "Live streaming, catch-up TV, nPVR, VOD, and CDN support",
        "24/7 monitoring with dedicated support team",
        "End-to-end signal acquisition and distribution"
      ]
    }
  ];

  const demoUses = [
    {
      title: "Server Side Ad Insertion (SSAI)",
      description: "Optimize your revenue with real-time server-side ad insertion with advanced demographic targeting",
      link: "/live",
      color: "#2563EB"
    },
    {
      title: "C2PA Content Authentication",
      description: "Ensure authenticity of your VOD assets with digital content origin verification",
      link: "/c2pa", 
      color: "#7C3AED"
    },
    {
      title: "AI Video Quality Enhancement",
      description: "Transform legacy content with AI-powered enhancement to 4K/60fps broadcast quality",
      link: "/ai-quality-enhancement",
      color: "#059669"
    },
    {
      title: "Automatic Live Captioning",
      description: "Real-time speech-to-text conversion with multi-language support and low latency integration",
      link: "/live-autocaptioning",
      color: "#DC2626"
    },
    {
      title: "Immersive subtitles",
      description: "Boost viewers’ engagement with AI-powered tool that detects emotions and creates visually expressive subtitles",
      link: "/immersive-subtitles",
      color: "#E11D48"
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ color: textDark }}>
      {/* Hero Section */}
      <section className={`mx-auto ${container} px-6 ${heroPadding}`}>
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6" style={{ color: brandBlue }}>
            Big Blue Marble
            <span className="block text-2xl md:text-3xl mt-3 font-normal" style={{ color: textDark, opacity: 0.85 }}>Professional Services</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8" style={{ opacity: 0.9 }}>
            Optimize and streamline your media infrastructure with custom solutions designed by AWS-certified engineers
          </p>
          <div className="flex flex-wrap gap-3 justify-center text-sm">
            {['AWS Media & Entertainment Competency', '9+ Years Cloud Experience', '100K+ Hours Migrated', 'Custom Streaming Solutions'].map((t,i)=> (
              <span key={i} className="rounded-full px-4 py-2 border transition-all duration-300 hover:scale-105" style={{ borderColor: brandBlue, color: brandBlue, background: `${brandBlue}05` }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Showcase */}
      <section id="demo-showcase" className="py-16 px-6" style={{ background: background }}>
        <div className={`mx-auto ${container}`}>
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: textDark }}>
          Interactive Demos: Test our solutions!
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {demoUses.map((demo, index) => (
              <a 
                key={index}
                href={demo.link}
                className="block p-6 rounded-2xl border transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{ 
                  borderColor: border, 
                  background: white,
                  textDecoration: 'none',
                  color: 'inherit'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = demo.color;
                  e.currentTarget.style.boxShadow = `0 12px 30px ${demo.color}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = border;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div 
                  className="w-3 h-3 rounded-full mb-4"
                  style={{ background: demo.color }}
                ></div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: textDark }}>
                  {demo.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ opacity: 0.8 }}>
                  {demo.description}
                </p>
                <div className="mt-4 text-sm font-medium" style={{ color: demo.color }}>
                  Try Demo →
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-6">
        <div className={`mx-auto ${container}`}>
          <h2 className="text-3xl font-bold text-center mb-4" style={{ color: textDark }}>
            Explore other services
          </h2>
          <p className="text-center text-lg mb-12 max-w-3xl mx-auto" style={{ opacity: 0.8 }}>
            Custom solutions for complex streaming challenges, designed by AWS-certified engineers with decades of video infrastructure experience
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="p-8 rounded-2xl border transition-all duration-300 hover:shadow-lg hover:scale-105"
                style={{ borderColor: border, background: white }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-3xl">{service.icon}</span>
                  <h3 className="text-2xl font-semibold" style={{ color: brandBlue }}>
                    {service.title}
                  </h3>
                </div>
                <p className="mb-6" style={{ opacity: 0.9 }}>
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div 
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ background: brandBlue }}
                      ></div>
                      <span className="text-sm leading-relaxed" style={{ opacity: 0.8 }}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AWS Competency Section */}
      <section className="py-16 px-6" style={{ background: `${brandBlue}10` }}>
        <div className={`mx-auto ${container}`}>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: textDark }}>
                AWS Advanced Tier Services Partner
              </h2>
              <p className="text-lg mb-6" style={{ opacity: 0.9 }}>
                We are one of the 20 companies in the world recognized as an AWS Media & Entertainment partner and have over a decade of experience with AWS Cloud, Edge- and Media Services.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ background: brandBlue }}></div>
                  <span>25+ AWS-certified engineers on board</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ background: brandBlue }}></div>
                  <span>2 products recognized as AWS Qualified Software</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ background: brandBlue }}></div>
                  <span>Highest AWS standards of reliability and performance.                  </span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ border: `1px solid ${border}` }}>
                <div 
                  className="p-8"
                  style={{ background: white }}
                >
                  <div className="mb-4 h-64 flex items-center justify-center">
                    <img 
                      src="/images/aws-partner-badge.png" 
                      alt="AWS Media & Entertainment Competency Badge" 
                      className="w-auto mx-auto object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 px-6">
        <div className={`mx-auto ${container}`}>
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: textDark }}>
            How We Work
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Initial Consultation", desc: "Comprehensive discussion about your requirements and technical challenges" },
              { step: "02", title: "Requirements Analysis", desc: "Deep dive into your technical needs, constraints, and business objectives" },
              { step: "03", title: "Solution Design", desc: "Custom solution architecture with detailed implementation timeline and milestones" },
              { step: "04", title: "Deployment & Support", desc: "Professional implementation with comprehensive ongoing support and monitoring" }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-xl font-bold"
                  style={{ background: brandBlue }}
                >
                  {phase.step}
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: textDark }}>
                  {phase.title}
                </h3>
                <p className="text-sm" style={{ opacity: 0.8 }}>
                  {phase.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6" style={{ background: brandBlue }}>
        <div className={`mx-auto ${container} text-center`}>
          <h2 className="text-3xl font-bold mb-4 text-white">
            Ready to Transform Your Media Infrastructure?
          </h2>
          <p className="text-xl mb-8 text-white opacity-90">
            Get professional support for your media infrastructure today. Our engineers will begin analyzing your requirements and designing the optimal solution immediately.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="https://www.bigbluemarble.com/en/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
              style={{ background: white, color: brandBlue, textDecoration: 'none' }}
            >
              Get in touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;